import { Link } from "react-router-dom";
import type { Video } from "@/mocks/videos";
import { formatDuration, formatViews, typeLabel, videoPath } from "@/lib/videos";

interface VideoCardProps {
  video: Video;
  showSummary?: boolean;
  rank?: number;
}

export default function VideoCard({ video, showSummary = false, rank }: VideoCardProps) {
  return (
    <Link
      to={videoPath(video.id)}
      className="group block rounded-lg overflow-hidden bg-background-100 border border-background-200/70 hover:border-primary-500/60 transition-colors duration-300 cursor-pointer"
    >
      <div className="relative w-full aspect-[3/4] overflow-hidden bg-background-200">
        <img
          src={video.cover}
          alt={`${video.title} - ${video.category} ${video.region} ${video.year} 封面`}
          title={`${video.title} ${video.category} 高清在线观看`}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-background-950/90 via-background-950/10 to-transparent" />
        <span className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-sm bg-primary-500 text-background-50 text-[11px] font-medium tracking-wide">
          {typeLabel(video.type)}
        </span>
        <span className="absolute top-2.5 right-2.5 px-1.5 py-0.5 rounded-sm bg-background-950/75 text-foreground-100 text-[11px] font-label tracking-wider">
          {formatDuration(video.duration)}
        </span>
        {typeof rank === "number" && (
          <span className="absolute bottom-2.5 left-2.5 font-heading text-3xl font-bold text-accent-500 leading-none">
            {String(rank).padStart(2, "0")}
          </span>
        )}
        <div className="absolute bottom-2.5 right-2.5 flex items-center gap-1 text-foreground-200 text-[11px]">
          <i className="ri-play-circle-fill text-accent-400" />
          {formatViews(video.views)}
        </div>
        <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <span className="w-12 h-12 rounded-full bg-primary-500/90 flex items-center justify-center text-background-50">
            <i className="ri-play-fill text-2xl" />
          </span>
        </div>
      </div>

      <div className="p-3 md:p-3.5">
        <h3 className="text-sm md:text-[15px] font-medium text-foreground-50 truncate group-hover:text-accent-400 transition-colors">
          {video.title}
        </h3>
        {showSummary && (
          <p className="mt-1.5 text-xs leading-relaxed text-foreground-500 line-clamp-2">{video.summary}</p>
        )}
        <div className="mt-2 flex items-center gap-2 text-[11px] text-foreground-500 flex-wrap">
          <span className="px-1.5 py-0.5 rounded-sm bg-secondary-800/70 text-secondary-200">{video.region}</span>
          <span>{video.year}</span>
          <span className="w-0.5 h-0.5 rounded-full bg-foreground-700" />
          <span className="flex items-center gap-0.5">
            <i className="ri-star-fill text-accent-400" />
            {video.rating.toFixed(1)}
          </span>
        </div>
      </div>
    </Link>
  );
}